import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, message } from 'antd';
import { ArrowLeftOutlined, BankOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postRequest } from '../../appRedux/actions/Axios';
import { GetBranches, setBranches } from '../../appRedux/actions/Selector/Branch';
import { setEncriptionBranch, userSignIn } from '../../appRedux/actions/Auth';
import { CleanNavBarItems } from '../../appRedux/actions';
import CircularProgress from 'components/CircularProgress/index';
import LoginHeader from '../Topbar/LoginHeader/index';
import CardSelector from './CardSelector';
import './companySelector.less';
import './branchSelector.less';

const BranchSelector = ({ location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { branchList } = useSelector(({ Branch }) => Branch);
  const { authUser, company, user } = useSelector(({ auth }) => auth);
  const [searchCriteria, setSearchCriteria] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const autoSelectionAttempted = useRef(false);
  const selectionInProgress = useRef(false);
  const companyId = location.state?.Company_ID || company?.id;
  const userRolePOS = user?.UserRolePOS;

  useEffect(() => {
    let isActive = true;

    if (!authUser) {
      history.replace('/signin');
      return undefined;
    }

    if (!companyId) {
      history.replace('/companySelector');
      return undefined;
    }

    autoSelectionAttempted.current = false;
    selectionInProgress.current = false;
    setSelectedBranchId(null);
    setIsLoading(true);
    dispatch(setBranches([]));

    dispatch(GetBranches({ CompanyId: companyId }))
      .then(() => {
        if (isActive) setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        if (isActive) {
          setIsLoading(false);
          message.error('No pudimos cargar tus sucursales. Inténtalo nuevamente.');
        }
      });

    return () => {
      isActive = false;
    };
  }, [authUser, companyId, dispatch, history]);

  const handleSelectBranch = useCallback(branch => {
    if (selectionInProgress.current) return;

    selectionInProgress.current = true;
    setSelectedBranchId(branch.id);
    dispatch(postRequest('/users/SelectBranch', { Branch_ID: branch.id }, {}))
      .then(response => {
        const data = response?.payload?.data;

        if (typeof data !== 'undefined') {
          dispatch(setEncriptionBranch(branch));
          dispatch(CleanNavBarItems());
          dispatch(userSignIn(data));
          history.push(userRolePOS === 1
            ? '/PointOfSale/Entry/PointOfSale02'
            : '/main/dashboard/crypto');
        } else {
          selectionInProgress.current = false;
          setSelectedBranchId(null);
          message.error('No pudimos abrir esta sucursal. Inténtalo nuevamente.');
        }
      })
      .catch(error => {
        console.log(error);
        selectionInProgress.current = false;
        setSelectedBranchId(null);
        message.error('No pudimos abrir esta sucursal. Inténtalo nuevamente.');
      });
  }, [dispatch, history, userRolePOS]);

  useEffect(() => {
    if (!isLoading && branchList.length === 1 && !autoSelectionAttempted.current) {
      autoSelectionAttempted.current = true;
      handleSelectBranch(branchList[0]);
    }
  }, [branchList, handleSelectBranch, isLoading]);

  const normalizedSearch = searchCriteria.trim().toLocaleUpperCase();
  const filteredBranches = branchList.filter(branch =>
    (branch.displayBranch || '').toLocaleUpperCase().includes(normalizedSearch)
  );
  const companyName = company?.displayCompany || branchList[0]?.displayCompany || 'Compañía seleccionada';
  const hasLargeBranchCards = branchList.length <= 12;

  return (
    <div className={`dasi-company-selector dasi-branch-selector${hasLargeBranchCards ? ' dasi-company-selector--large-cards' : ''}`}>
      <LoginHeader compact />

      <main className="dasi-company-selector__main">
        <header className="dasi-company-selector__hero">
          <div>
            <button
              type="button"
              className="dasi-company-selector__back"
              onClick={() => history.push('/companySelector')}>
              <ArrowLeftOutlined />
              <span>Volver a compañías</span>
            </button>
            <span className="dasi-company-selector__step">Paso 3 de 3</span>
            <h1>Elige una sucursal</h1>
          </div>
        </header>

        <section className="dasi-company-selector__panel" aria-label="Sucursales disponibles">
          <div className="dasi-company-selector__toolbar">
            <Input
              value={searchCriteria}
              onChange={event => setSearchCriteria(event.target.value)}
              prefix={<SearchOutlined />}
              placeholder="Buscar sucursal por nombre"
              allowClear
              size="large"
              aria-label="Buscar sucursal"
            />

            <div className="dasi-branch-selector__company" title={companyName}>
              <BankOutlined />
              <span>
                <small>Compañía activa</small>
                <strong>{companyName}</strong>
              </span>
            </div>
            <div className="dasi-branch-selector__count" aria-label={`${branchList.length} sucursales disponibles`}>
              <strong>{branchList.length}</strong>
              <span>{branchList.length === 1 ? 'sucursal' : 'sucursales'}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="dasi-company-selector__loading" aria-live="polite">
              <CircularProgress />
              <p>Preparando tus sucursales…</p>
            </div>
          ) : filteredBranches.length > 0 ? (
            <div className="dasi-company-selector__grid">
              {filteredBranches.map(branch => (
                <CardSelector
                  key={branch.id}
                  Entity={branch}
                  SelectEntity={handleSelectBranch}
                  type={2}
                  disabled={selectedBranchId !== null}
                  isSelected={selectedBranchId === branch.id}
                />
              ))}
            </div>
          ) : (
            <div className="dasi-company-selector__empty">
              <span><SearchOutlined /></span>
              <h2>{normalizedSearch ? 'No encontramos coincidencias' : 'No hay sucursales disponibles'}</h2>
              <p>
                {normalizedSearch
                  ? 'Prueba buscando con otro nombre.'
                  : 'Cuando tengas una sucursal asignada, aparecerá aquí.'}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default BranchSelector;
